import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Camera, ChevronRight, Image as ImageIcon, BookOpen, Hash, Menu, Loader2, X, CheckCircle, ArrowLeft, HelpCircle, FileText } from 'lucide-react';
import Joyride, { CallBackProps, STATUS, Step, ACTIONS } from 'react-joyride';
import { CameraCapture } from './camera/CameraCapture';
import { PreviewModal } from './preview/PreviewModal';
import { PdfPreviewModal } from './preview/PdfPreviewModal';
import ScanningSidebar from './ScanningSidebar';
import { TourTooltip } from '../common/TourTooltip';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { resetUpload } from './ScanningSlice';

interface ScanningProps { }

// RecentUpload interface imported from ScanningSidebar

export const Scanning: React.FC<ScanningProps> = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { isUploading, uploadProgress, uploadError, currentStep } = useAppSelector(
        (state) => state.scanning
    );

    const [subjectName, setSubjectName] = useState('');
    const [subjectCode, setSubjectCode] = useState('');
    // Removed isConfirmed state as per request
    const [view, setView] = useState<'selection' | 'camera'>('selection');
    const [error, setError] = useState('');
    const [subjectNameError, setSubjectNameError] = useState('');
    const [subjectCodeError, setSubjectCodeError] = useState('');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // New state for captured images flow
    const [capturedImages, setCapturedImages] = useState<string[]>([]);
    const [showPreview, setShowPreview] = useState(false);

    // File upload handler
    // New state for file uploads
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [showPdfPreview, setShowPdfPreview] = useState(false);

    // Tour state
    const [runTour, setRunTour] = useState(false);
    const [tourSteps, setTourSteps] = useState<Step[]>([]);

    useEffect(() => {
        const isMobile = window.innerWidth < 768;
        const steps: Step[] = [
            {
                target: '.tour-subject-input',
                title: 'Subject Details',
                content: 'Start by entering the name of the subject (e.g., Mathematics, Physics).',
                disableBeacon: true,
            },
            {
                target: '.tour-paper-code-input',
                title: 'Paper Code',
                content: 'Enter the unique code for this examination paper to track it correctly.',
                disableBeacon: true,
            },
            {
                target: '.tour-camera-option',
                title: 'Scan with Camera',
                content: 'Use your device camera to instantly capture and upload answer sheets.',
                disableBeacon: true,
            },
            {
                target: '.tour-upload-pdf',
                title: 'Upload File',
                content: 'Have a PDF already? Upload it directly from here.',
                disableBeacon: true,
            },
            {
                target: isMobile ? '.tour-mobile-menu' : '.tour-sidebar',
                title: 'Recent Uploads',
                content: 'View your previously scanned papers and their status here.',
                disableBeacon: true,
            }
        ];
        setTourSteps(steps);
    }, []);

    useEffect(() => {
        const hasSeenTour = localStorage.getItem('hasSeenScanningTour');
        if (!hasSeenTour) {
            setRunTour(true);
        }
    }, []);

    const handleJoyrideCallback = (data: CallBackProps) => {
        const { status, action } = data;
        const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
        if (finishedStatuses.includes(status) || action === ACTIONS.CLOSE) {
            setRunTour(false);
            localStorage.setItem('hasSeenScanningTour', 'true');
        }
    };

    // Unified file handling
    const processFiles = (files: File[]) => {
        if (!subjectName || !subjectCode) {
            setError('Please enter Subject Name and Paper Code first');
            return;
        }

        if (subjectNameError || subjectCodeError) {
            setError('Please fix the errors before proceeding');
            return;
        }

        const validFiles = Array.from(files).filter(file => {
            const extension = file.name.split('.').pop()?.toLowerCase();
            return ['pdf', 'ppt', 'pptx'].includes(extension || '') ||
                file.type === 'application/pdf' ||
                file.type.includes('presentation');
        });

        if (validFiles.length > 0) {
            setSelectedFiles(prev => [...prev, ...validFiles]);
            setError('');
        }
    };

    // File upload handler - NOW SHOWS PREVIEW BOX ON PAGE
    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;
        processFiles(Array.from(files));
    };

    // Drag and Drop handlers
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            processFiles(Array.from(e.dataTransfer.files));
        }
    };

    // Generic Success Modal Component
    const SuccessModal = ({ onClose }: { onClose: () => void }) => (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-card w-full max-w-md rounded-2xl shadow-2xl border border-border overflow-hidden relative"
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 hover:bg-secondary rounded-full transition-colors text-muted-foreground hover:text-foreground"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="p-8 flex flex-col items-center text-center">
                    <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6 text-green-600 dark:text-green-400">
                        <CheckCircle className="w-10 h-10" />
                    </div>

                    <h2 className="text-2xl font-bold mb-2">Upload Successful!</h2>
                    <p className="text-muted-foreground mb-8">
                        Your answer sheet has been securely uploaded and queued for evaluation.
                    </p>

                    <div className="flex flex-col gap-3 w-full">
                        <button
                            onClick={onClose}
                            className="w-full py-4 rounded-xl bg-primary text-primary-foreground font-extrabold shadow-lg shadow-primary/20 hover:shadow-primary/40 hover:-translate-y-0.5 active:scale-95 transition-all flex items-center justify-center gap-2"
                        >
                            <Camera className="w-5 h-5" />
                            Scan Next Copy
                        </button>
                        <button
                            onClick={() => {
                                handleSuccessClose();
                                navigate('/evaluator');
                            }}
                            className="w-full py-4 rounded-xl border-2 border-primary text-primary font-extrabold hover:bg-primary/5 active:scale-95 transition-all flex items-center justify-center gap-2"
                        >
                            Start Evaluate
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );

    const handleSuccessClose = () => {
        dispatch(resetUpload());
        setCapturedImages([]);
        setSelectedFiles([]);
        setShowPdfPreview(false);
        // Keep Subject Name and Code for efficient batch scanning
        // setSubjectName('');
        // setSubjectCode('');
    };

    const handleSubjectNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSubjectName(value);

        if (value.length > 100) {
            setSubjectNameError('Max 100 characters allowed');
        } else if (!/^[a-zA-Z0-9\s]*$/.test(value)) {
            setSubjectNameError('Special characters not allowed');
        } else {
            setSubjectNameError('');
            if (!subjectCodeError) setError('');
        }
    };

    const handleSubjectCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSubjectCode(value);

        if (value.length > 15) {
            setSubjectCodeError('Max 15 characters allowed');
        } else if (!/^[a-zA-Z0-9]*$/.test(value)) {
            setSubjectCodeError('Only alphanumeric characters (A-Z, 0-9) allowed');
        } else {
            setSubjectCodeError('');
            if (!subjectNameError) setError('');
        }
    };

    // Removed handleConfirm

    const handleCameraStart = () => {
        if (!subjectName || !subjectCode) {
            setError('Please enter Subject Name and Paper Code first');
            return;
        }
        if (subjectNameError || subjectCodeError) {
            setError('Please fix the errors before proceeding');
            return;
        }
        setView('camera');
    };

    const handleBackToSelection = () => {
        setView('selection');
    };

    const handleCaptureComplete = (images: string[]) => {
        setCapturedImages(images);
        setView('selection');
    };

    if (view === 'camera') {
        return (
            <CameraCapture
                onBack={handleBackToSelection}
                onCaptureComplete={handleCaptureComplete}
                subjectName={subjectName}
                subjectCode={subjectCode}
                initialImages={capturedImages}
            />
        );
    }

    return (
        <div className="flex h-screen bg-[#111111] overflow-hidden font-sans relative">

            {/* Mobile Sidebar Overlay */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/60 z-30 md:hidden backdrop-blur-sm"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Sidebar Wrapper for Mobile/Desktop */}
            <div className={`tour-sidebar
                fixed md:static inset-y-0 left-0 z-40 w-80 md:w-[400px] bg-[#111111] transform transition-transform duration-300 ease-in-out
                ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
            `}>
                <ScanningSidebar onClose={() => setIsSidebarOpen(false)} />
            </div>

            {/* Main Content Area - White Rounded Card */}
            <div className="flex-1 m-0 md:my-2 md:mr-2 bg-background md:rounded-2xl overflow-hidden shadow-2xl flex flex-col relative z-10 w-full">
                <main className="flex-1 p-3 md:p-12 pt-4 md:pt-10 overflow-y-auto flex flex-col items-center justify-start md:justify-center relative bg-white dark:bg-card">

                    {/* Desktop Back Button - Left Side */}
                    <div className="hidden md:block absolute top-8 left-8 z-20">
                        <Link
                            to="/home"
                            className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-gray-500 hover:text-gray-900 bg-gray-100/50 hover:bg-gray-100 rounded-full transition-all"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span>Back</span>
                        </Link>
                    </div>

                    {/* Tutorial Button (Desktop) */}
                    <div className="hidden md:block absolute top-8 right-8 z-20">
                        <button
                            onClick={() => setRunTour(true)}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-gray-500 hover:text-primary bg-gray-100/50 hover:bg-primary/10 rounded-full transition-all"
                        >
                            <HelpCircle className="w-4 h-4" />
                            <span>Tutorial</span>
                        </button>
                    </div>

                    {/* Mobile Header - Menu then Back Button on Left */}
                    <div className="w-full flex justify-between md:hidden items-center gap-3 mb-4 pt-2">

                        <Link
                            to="/home"
                            className="p-2 flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-gray-900 bg-gray-100/50 hover:bg-gray-100 rounded-full transition-all"
                        >
                            <ArrowLeft className="w-5 h-5" /> Back
                        </Link>

                        <div className="flex items-center gap-2">
                            {/* Tutorial Button (Mobile) */}
                            <button
                                onClick={() => setRunTour(true)}
                                className="p-2 text-gray-600 hover:text-primary hover:bg-gray-100 rounded-full transition-colors bg-gray-100/50"
                            >
                                <HelpCircle className="w-5 h-5" />
                            </button>

                            <button
                                onClick={() => setIsSidebarOpen(true)}
                                className="tour-mobile-menu p-2 text-gray-600 hover:text-primary hover:bg-gray-100 rounded-full transition-colors bg-gray-100/50"
                            >
                                <Menu className="w-5 h-5" />
                            </button>
                        </div>

                    </div>

                    <div className="w-full max-w-2xl">

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-4 text-center"
                        >
                            <h1 className="text-3xl md:text-5xl font-extrabold mb-1 md:mb-3 text-gray-900 dark:text-white">
                                Scan Answer Sheet
                            </h1>

                        </motion.div>

                        <div className="space-y-4 md:space-y-6">
                            {/* Step 1: Subject Details - Modernized */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-1 md:mb-2">
                                <div className="space-y-1.5 md:space-y-2">
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 ml-1">Subject Name</label>
                                    <div className="relative group">
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary transition-colors">
                                            <BookOpen className="w-5 h-5" />
                                        </div>
                                        <input
                                            type="text"
                                            className={`tour-subject-input w-full pl-12 pr-4 py-3 md:py-4 rounded-xl bg-gray-50/50 dark:bg-secondary/30 border transition-all duration-300 font-medium placeholder:text-muted-foreground/40 shadow-sm outline-none
                                                ${subjectNameError
                                                    ? 'border-red-500 focus:border-red-500 focus:ring-4 focus:ring-red-500/10'
                                                    : 'border-gray-200 dark:border-border hover:border-primary/50 focus:border-primary focus:ring-4 focus:ring-primary/10'
                                                }`}
                                            placeholder="e.g. Physics"
                                            value={subjectName}
                                            onChange={handleSubjectNameChange}
                                        />
                                    </div>
                                    <p className={`text-xs ml-1 transition-colors ${subjectNameError ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
                                        {subjectNameError || 'Only characters allowed'}
                                    </p>
                                </div>

                                <div className="space-y-1.5 md:space-y-2">
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 ml-1">Paper Code</label>
                                    <div className="relative group">
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary transition-colors">
                                            <Hash className="w-5 h-5" />
                                        </div>
                                        <input
                                            type="text"
                                            className={`tour-paper-code-input w-full pl-12 pr-4 py-3 md:py-4 rounded-xl bg-gray-50/50 dark:bg-secondary/30 border transition-all duration-300 font-medium placeholder:text-muted-foreground/40 shadow-sm outline-none
                                                ${subjectCodeError
                                                    ? 'border-red-500 focus:border-red-500 focus:ring-4 focus:ring-red-500/10'
                                                    : 'border-gray-200 dark:border-border hover:border-primary/50 focus:border-primary focus:ring-4 focus:ring-primary/10'
                                                }`}
                                            placeholder="e.g. GS001"
                                            value={subjectCode}
                                            onChange={handleSubjectCodeChange}
                                        />
                                    </div>
                                    <p className={`text-xs ml-1 transition-colors ${subjectCodeError ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
                                        {subjectCodeError || 'Alphanumeric only, no spaces'}
                                    </p>
                                </div>
                            </div>

                            {/* Error Message */}
                            <AnimatePresence>
                                {error && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="text-red-500 text-sm text-center font-medium bg-red-50 dark:bg-red-900/10 py-2 rounded-lg"
                                    >
                                        {error}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Upload Progress Section */}
                            <AnimatePresence>
                                {isUploading && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        className="bg-card border border-border rounded-2xl p-6 shadow-lg"
                                    >
                                        <div className="flex items-center gap-4 mb-4">
                                            <Loader2 className="w-6 h-6 text-primary animate-spin" />
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-lg">Uploading Files...</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    {currentStep === 'uploading' && uploadProgress < 33 && 'Getting upload URLs...'}
                                                    {currentStep === 'uploading' && uploadProgress >= 33 && uploadProgress < 66 && 'Uploading to cloud...'}
                                                    {currentStep === 'uploading' && uploadProgress >= 66 && 'Saving metadata...'}
                                                </p>
                                            </div>
                                            <span className="text-sm font-medium text-primary">{uploadProgress}%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                                            <motion.div
                                                className="h-full bg-gradient-to-r from-primary to-gray-800"
                                                initial={{ width: 0 }}
                                                animate={{ width: `${uploadProgress}%` }}
                                                transition={{ duration: 0.3 }}
                                            />
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Upload Error from Redux */}
                            <AnimatePresence>
                                {uploadError && currentStep === 'error' && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="text-red-500 text-sm text-center font-medium bg-red-50 dark:bg-red-900/10 py-2 rounded-lg"
                                    >
                                        {uploadError}
                                    </motion.div>
                                )}
                            </AnimatePresence>



                            {/* Step 2: Unified Camera / Upload / Review Card */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.1 }}
                                className="flex flex-col items-center gap-4 w-full"
                            >
                                <div
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onClick={
                                        capturedImages.length > 0
                                            ? () => setShowPreview(true)
                                            : selectedFiles.length > 0
                                                ? () => setShowPdfPreview(true)
                                                : handleCameraStart
                                    }
                                    className={`tour-camera-option group relative overflow-hidden rounded-3xl border-2 border-dashed transition-all duration-300 p-4 md:p-10 flex flex-col items-center text-center w-full max-w-2xl cursor-pointer
                                        ${(capturedImages.length > 0 || selectedFiles.length > 0)
                                            ? 'border-primary/30 bg-white shadow-xl shadow-primary/5'
                                            : 'bg-white border-border hover:border-primary/50 hover:shadow-xl hover:shadow-primary/5'
                                        }`}
                                >


                                    {/* State Management Icons */}
                                    <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mb-6 border-2 transition-transform group-hover:scale-110
                                        ${capturedImages.length > 0
                                            ? 'bg-white border-primary/20 text-primary'
                                            : selectedFiles.length > 0
                                                ? 'bg-white border-gray-400 text-gray-800'
                                                : 'bg-white border-gray-200 text-gray-600'
                                        }`}>
                                        {capturedImages.length > 0 ? (
                                            <ImageIcon className="w-10 h-10" />
                                        ) : selectedFiles.length > 0 ? (
                                            <FileText className="w-10 h-10" />
                                        ) : (
                                            <Camera className="w-10 h-10" />
                                        )}
                                    </div>

                                    {/* Text Content */}
                                    {capturedImages.length > 0 ? (
                                        <>
                                            <h3 className="text-2xl font-bold mb-2">{capturedImages.length} Pages Captured</h3>
                                            <p className="text-muted-foreground mb-8">Your pages are ready for final review and submission.</p>
                                        </>
                                    ) : selectedFiles.length > 0 ? (
                                        <>
                                            <h3 className="text-2xl font-bold mb-2">{selectedFiles.length} PDF File{selectedFiles.length !== 1 ? 's' : ''} Selected</h3>
                                            <p className="text-muted-foreground mb-8">Ready for review and evaluation.</p>
                                        </>
                                    ) : (
                                        <>
                                            <h3 className="text-2xl font-bold mb-2">Use Camera</h3>
                                            <p className="text-sm text-muted-foreground mb-8">
                                                Capture images of your answer sheets to upload.
                                            </p>
                                        </>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="w-full flex flex-col sm:flex-row items-center justify-center gap-3">
                                        {(capturedImages.length > 0 || selectedFiles.length > 0) && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCapturedImages([]);
                                                    setSelectedFiles([]);
                                                }}
                                                disabled={isUploading}
                                                className={`w-full sm:w-auto px-6 py-3 rounded-xl border border-red-200 dark:border-red-900/30 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors font-bold ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                Discard
                                            </button>
                                        )}

                                        <button
                                            disabled={isUploading}
                                            className={`w-full sm:w-auto px-10 py-3 rounded-xl bg-primary text-primary-foreground font-bold shadow-lg shadow-primary/20 hover:shadow-primary/40 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            {capturedImages.length > 0 ? (
                                                <>Review Captured <ChevronRight className="w-4 h-4" /></>
                                            ) : selectedFiles.length > 0 ? (
                                                <>Review & Upload <ChevronRight className="w-4 h-4" /></>
                                            ) : (
                                                <>Start Camera</>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Small Upload PDF link - only show if nothing selected */}
                                {capturedImages.length === 0 && selectedFiles.length === 0 && (
                                    <div className="w-full flex justify-end max-w-2xl px-2">
                                        <label className="tour-upload-pdf text-sm text-primary hover:text-primary/80 cursor-pointer font-medium underline underline-offset-2 flex items-center gap-2">
                                            <span>Or Upload PDF</span>
                                            <input
                                                type="file"
                                                accept=".pdf,.ppt,.pptx,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                                                multiple
                                                className="hidden"
                                                onChange={handleFileUpload}
                                                onClick={(e) => {
                                                    (e.target as HTMLInputElement).value = '';
                                                    if (!subjectName || !subjectCode) {
                                                        e.preventDefault();
                                                        setError('Please enter Subject Name and Paper Code first');
                                                        return;
                                                    }
                                                    if (subjectNameError || subjectCodeError) {
                                                        e.preventDefault();
                                                        setError('Please fix the errors before proceeding');
                                                        return;
                                                    }
                                                }}
                                            />
                                        </label>
                                    </div>
                                )}
                            </motion.div>
                        </div>
                    </div>

                </main>
            </div>


            {/* Success Modal Overlay */}
            <AnimatePresence>
                {currentStep === 'success' && (
                    <SuccessModal onClose={handleSuccessClose} />
                )}
            </AnimatePresence>

            {/* Preview Modal moved here to ensure it overlays everything */}
            <PreviewModal
                isOpen={showPreview}
                initialImages={capturedImages}
                onClose={() => setShowPreview(false)}
                onBack={() => {
                    setShowPreview(false);
                }}
                subjectName={subjectName}
                subjectCode={subjectCode}
            />

            {/* PDF Preview Modal for uploaded PDFs */}
            <PdfPreviewModal
                isOpen={showPdfPreview}
                files={selectedFiles}
                onClose={() => {
                    setShowPdfPreview(false);
                    // Don't clear selectedFiles - let user go back and still see them
                }}
                onBack={() => {
                    setShowPdfPreview(false);
                    // Don't clear selectedFiles - let user go back and still see them
                }}
                subjectName={subjectName}
                subjectCode={subjectCode}
            />

            <Joyride
                steps={tourSteps}
                run={runTour}
                continuous
                showProgress
                showSkipButton
                tooltipComponent={TourTooltip}
                callback={handleJoyrideCallback}
                disableScrollParentFix={true}
                disableScrolling={true}
                floaterProps={{
                    disableAnimation: true,
                }}
                styles={{
                    spotlight: {
                        borderRadius: '16px',
                    },
                    overlay: {
                        backgroundColor: 'rgba(0, 0, 0, 0.65)',
                    },
                    options: {
                        zIndex: 10000,
                    }
                }}
            />
        </div>
    );
};
